import React, { useState, useMemo, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Search,
  Calendar,
  Grid3X3,
  List,
  Download,
  Eye,
  Clock,
  CheckCircle,
  XCircle,
  FileText,
} from "lucide-react";
import { PageHeader } from "@/components/PageHeader";
import { BreadcrumbItem } from "@/components/Breadcrumb";
import { Helmet } from "react-helmet-async";
import { useTranslation } from "react-i18next";
import BatchCard, { BatchPayment } from "./components/BatchCard";
import { getListBatchPayment } from "@/api/Admin/Profit";
import Spinner from "@/components/Spinner";

type BatchStatus = "draft" | "processing" | "completed" | "failed";

const BatchHistory: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [searchTerm, setSearchTerm] = useState("");
  const [monthFilter, setMonthFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [viewMode, setViewMode] = useState<"grid" | "list">("list");
  const [batchData, setBatchData] = useState<BatchPayment[]>([]);
  const [loading, setLoading] = useState(false);
  const [apiStats, setApiStats] = useState<any>(null);

  const fetchData = async () => {
    setLoading(true);
    try {
      let period = "";
      if (monthFilter) {
        const [month, year] = monthFilter.split("/");
        period = `${year}-${month}`;
      }

      const res = await getListBatchPayment({
        page: 1,
        limit: 100,
        status: statusFilter,
        search: searchTerm,
        period: period,
      });

      if (res?.data?.list?.data) {
        setApiStats(res.data.stats);
        const mappedData: BatchPayment[] = res.data.list.data.map((item: any) => ({
          id: item.id.toString(),
          code: item.batch_code || `BATCH-${item.id}`,
          name: item.title,
          month: item.created_at
            ? `${item.created_at.split("-")[1]}/${item.created_at.split("-")[0]}`
            : "N/A",
          status: item.status as BatchStatus,
          totalPayments: item.profits_count || item.total_items || 0,
          totalAmount: Number(item.total_amount) || 0,
          createdBy: item.admin_name || "Admin",
          createdAt: item.created_at,
          completedAt: item.updated_at,
          note: item.note,
        }));
        setBatchData(mappedData);
      } else if (res?.data?.data) {
        // Fallback for different API structure
        const mappedData: BatchPayment[] = res.data.data.map((item: any) => ({
          id: item.id.toString(),
          code: item.batch_code || `BATCH-${item.id}`,
          name: item.title,
          month: item.period ? `${item.period.split("-")[1]}/${item.period.split("-")[0]}` : "N/A",
          status: item.status as BatchStatus,
          totalPayments: item.profits_count || item.total_records || 0,
          totalAmount: Number(item.total_amount) || 0,
          createdBy: item.admin_name || "Admin",
          createdAt: item.created_at,
          completedAt: item.updated_at,
          note: item.note,
        }));
        setBatchData(mappedData);
      }
    } catch (error) {
      console.error("Error fetching batches:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [searchTerm, monthFilter, statusFilter]);

  const breadcrumbs: BreadcrumbItem[] = [
    { label: "Dashboard", path: "/admin/dashboard" },
    { label: t("batchHistory.title") },
  ];

  const filteredData = batchData;

  // Calculate stats
  const stats = useMemo(() => {
    if (apiStats) {
      return {
        total: apiStats.total_batches,
        draft: apiStats.count_draft,
        processing: apiStats.count_processing,
        completed: apiStats.count_completed,
        failed: apiStats.count_failed,
        totalAmount: Number(apiStats.total_amount),
      };
    }
    const total = batchData.length;
    const draft = batchData.filter((b) => b.status === "draft").length;
    const processing = batchData.filter((b) => b.status === "processing").length;
    const completed = batchData.filter((b) => b.status === "completed").length;
    const failed = batchData.filter((b) => b.status === "failed").length;
    const totalAmount = batchData.reduce((sum, b) => sum + b.totalAmount, 0);
    return { total, draft, processing, completed, failed, totalAmount };
  }, [batchData, apiStats]);

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat("vi-VN").format(amount) + " đ";
  };

  const getStatusBadge = (status: BatchStatus) => {
    const badges = {
      draft: {
        bg: "bg-gray-100 dark:bg-gray-700",
        text: "text-gray-700 dark:text-gray-300",
        icon: FileText,
        label: t("batchHistory.statusDraft"),
      },
      processing: {
        bg: "bg-blue-100 dark:bg-blue-900/30",
        text: "text-blue-700 dark:text-blue-400",
        icon: Clock,
        label: t("batchHistory.statusProcessing"),
      },
      completed: {
        bg: "bg-green-100 dark:bg-green-900/30",
        text: "text-green-700 dark:text-green-400",
        icon: CheckCircle,
        label: t("batchHistory.statusCompleted"),
      },
      failed: {
        bg: "bg-red-100 dark:bg-red-900/30",
        text: "text-red-700 dark:text-red-400",
        icon: XCircle,
        label: t("batchHistory.statusFailed"),
      },
    };
    const badge = badges[status];
    const Icon = badge.icon;
    return (
      <span
        className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${badge.bg} ${badge.text}`}
      >
        <Icon className="w-3.5 h-3.5" />
        {badge.label}
      </span>
    );
  };

  const generateMonthOptions = () => {
    const options = [];
    const currentDate = new Date();
    for (let i = 0; i < 12; i++) {
      const date = new Date(currentDate.getFullYear(), currentDate.getMonth() - i, 1);
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const year = date.getFullYear();
      options.push({
        value: `${month}/${year}`,
        label: `Tháng ${month}/${year}`,
      });
    }
    return options;
  };

  const handleViewBatch = (batchId: string) => {
    navigate(`/admin/batch-history/${batchId}`);
  };

  const handleDownloadBatch = (batchId: string) => {
    // api to download batch report
    console.log("Download batch:", batchId);
  };

  return (
    <div className="dark:bg-dark-bg">
      <Helmet>
        <title>{t("batchHistory.title")} - Admin Dashboard</title>
      </Helmet>

      <PageHeader
        title={t("batchHistory.title")}
        description={t("batchHistory.subtitle")}
        breadcrumbs={breadcrumbs}
      />

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-6">
        <div className="bg-white dark:bg-gray-800 rounded-md p-4 border border-gray-200 dark:border-gray-700">
          <p className="text-sm text-gray-500 dark:text-gray-400">{t("batchHistory.totalBatches")}</p>
          <p className="text-2xl font-bold text-gray-900 dark:text-white mt-1">{stats.total}</p>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-md p-4 border border-gray-200 dark:border-gray-700">
          <p className="text-sm text-gray-500 dark:text-gray-400">{t("batchHistory.statusDraft")}</p>
          <p className="text-2xl font-bold text-gray-600 dark:text-gray-300 mt-1">{stats.draft}</p>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-md p-4 border border-gray-200 dark:border-gray-700">
          <p className="text-sm text-gray-500 dark:text-gray-400">{t("batchHistory.statusProcessing")}</p>
          <p className="text-2xl font-bold text-blue-600 dark:text-blue-400 mt-1">{stats.processing}</p>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-md p-4 border border-gray-200 dark:border-gray-700">
          <p className="text-sm text-gray-500 dark:text-gray-400">{t("batchHistory.statusCompleted")}</p>
          <p className="text-2xl font-bold text-green-600 dark:text-green-400 mt-1">{stats.completed}</p>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-md p-4 border border-gray-200 dark:border-gray-700">
          <p className="text-sm text-gray-500 dark:text-gray-400">{t("batchHistory.statusFailed")}</p>
          <p className="text-2xl font-bold text-red-600 dark:text-red-400 mt-1">{stats.failed}</p>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-md p-4 border border-gray-200 dark:border-gray-700">
          <p className="text-sm text-gray-500 dark:text-gray-400">{t("batchHistory.totalAmount")}</p>
          <p className="text-2xl font-bold text-green-600 dark:text-green-400 mt-1">{formatCurrency(stats.totalAmount)}</p>
        </div>
      </div>

      {/* Filters */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <div className="md:col-span-2">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            <Search className="w-4 h-4 inline mr-1" />
            {t("batchHistory.search")}
          </label>
          <input
            type="text"
            placeholder={t("batchHistory.searchPlaceholder")}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full px-4 py-2.5 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent text-sm"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            <Calendar className="w-4 h-4 inline mr-1" />
            {t("batchHistory.month")}
          </label>
          <select
            value={monthFilter}
            onChange={(e) => setMonthFilter(e.target.value)}
            className="w-full px-4 py-2.5 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent text-sm"
          >
            <option value="">{t("batchHistory.allMonths")}</option>
            {generateMonthOptions().map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            {t("batchHistory.status")}
          </label>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="w-full px-4 py-2.5 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent text-sm"
          >
            <option value="">{t("batchHistory.allStatuses")}</option>
            <option value="draft">{t("batchHistory.statusDraft")}</option>
            <option value="processing">{t("batchHistory.statusProcessing")}</option>
            <option value="completed">{t("batchHistory.statusCompleted")}</option>
            <option value="failed">{t("batchHistory.statusFailed")}</option>
          </select>
        </div>
      </div>

      {/* View Toggle and Count */}
      <div className="flex items-center justify-between mb-4">
        <p className="text-sm text-gray-600 dark:text-gray-400">
          {t("batchHistory.showing")} <span className="font-semibold">{filteredData.length}</span> batch
        </p>
        <div className="flex items-center gap-1 bg-white shadow-lg dark:bg-gray-700 rounded-md p-1">
          <button
            onClick={() => setViewMode("list")}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${viewMode === "list"
              ? "bg-primary text-white"
              : "text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600"
              }`}
          >
            <List className="w-4 h-4" />
            {t("batchHistory.listView")}
          </button>
          <button
            onClick={() => setViewMode("grid")}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${viewMode === "grid"
              ? "bg-primary text-white"
              : "text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600"
              }`}
          >
            <Grid3X3 className="w-4 h-4" />
            {t("batchHistory.gridView")}
          </button>
        </div>
      </div>

      {/* Batch Content */}
      {loading ? (
        <div className="flex justify-center items-center py-20 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
          <Spinner size={40} />
        </div>
      ) : (
        <>
          {filteredData.length === 0 ? (
            <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-12 text-center">
              <FileText className="w-12 h-12 text-gray-300 dark:text-gray-600 mx-auto mb-4" />
              <p className="text-gray-500 dark:text-gray-400 font-medium">
                {t("batchHistory.noDataFound") || "No batch payments found"}
              </p>
              <p className="text-sm text-gray-400 dark:text-gray-500 mt-1">
                Try adjusting your search or filters to find what you're looking for.
              </p>
            </div>
          ) : (
            <>
              {/* Batch Cards - Grid View */}
              {viewMode === "grid" && (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {filteredData.map((batch) => (
                    <BatchCard
                      key={batch.id}
                      batch={batch}
                      onView={handleViewBatch}
                      onDownload={handleDownloadBatch}
                    />
                  ))}
                </div>
              )}

              {/* Batch List - List View */}
              {viewMode === "list" && (
                <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="bg-gray-50 dark:bg-gray-700/50">
                          <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase">
                            {t("batchHistory.batchCode")}
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase">
                            {t("batchHistory.batchName")}
                          </th>
                          <th className="px-4 py-3 text-center text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase">
                            {t("batchHistory.month")}
                          </th>
                          <th className="px-4 py-3 text-center text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase">
                            {t("batchHistory.status")}
                          </th>
                          <th className="px-4 py-3 text-center text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase">
                            {t("batchHistory.numPayments")}
                          </th>
                          <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase">
                            {t("batchHistory.totalAmount")}
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase">
                            {t("batchHistory.createdBy")}
                          </th>
                          <th className="px-4 py-3 text-center text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase">
                            {t("batchHistory.actions")}
                          </th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                        {filteredData.map((batch) => (
                          <tr
                            key={batch.id}
                            className="hover:bg-gray-50 dark:hover:bg-gray-700/30 transition-colors"
                          >
                            <td className="px-4 py-3 text-sm font-mono text-gray-500 dark:text-gray-400">
                              {batch.code}
                            </td>
                            <td className="px-4 py-3">
                              <p className="text-sm font-medium text-gray-900 dark:text-white">{batch.name}</p>
                              <p className="text-xs text-gray-500 dark:text-gray-400">{batch.createdAt?.split("T")[0].split("-").reverse().join("-")}</p>
                            
                            </td>
                            <td className="px-4 py-3 text-center text-sm text-gray-700 dark:text-gray-300">
                              {batch.month}
                            </td>
                            <td className="px-4 py-3 text-center">{getStatusBadge(batch.status)}</td>
                            <td className="px-4 py-3 text-center text-sm font-medium text-primary">
                              {batch.totalPayments}
                            </td>
                            <td className="px-4 py-3 text-right text-sm font-bold text-green-600 dark:text-green-400">
                              {formatCurrency(batch.totalAmount)}
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400">
                              {batch.createdBy}
                            </td>
                            <td className="px-4 py-3 text-center">
                              <div className="flex items-center justify-center gap-2">
                                <button
                                  onClick={() => handleViewBatch(batch.id)}
                                  className="p-2 text-primary hover:bg-primary/10 rounded-md transition-colors"
                                >
                                  <Eye className="w-4 h-4" />
                                </button>
                                <button
                                  onClick={() => handleDownloadBatch(batch.id)}
                                  className="p-2 text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md transition-colors"
                                >
                                  <Download className="w-4 h-4" />
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </>
          )}
        </>
      )}
    </div>
  );
};

export default BatchHistory;
